import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';

import { buscarCortes } from '../../actions/cortes';
import { buscarSucursales } from '../../actions/sucursales';
import { buscarEmpleados } from '../../actions/empleados';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import { formatNumber } from '../../utils/generalFunctions';

import FiltrosCorte from './FiltrosCorte';

const formatNombre = (persona) => persona
    ? `${persona.nombre || ''} ${persona.apellido_paterno || ''} ${persona.apellido_materno || ''}`.trim()
    : '-';

const formatFecha = (fecha) => fecha
    ? new Date(fecha).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

const filtrosVacios = {
    folio: '',
    responsable: null,
    sucursal: null,
    status: null,
    fecha_inicio: null,
    fecha_fin: null
};

const Cortes = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { listado } = useSelector(state => state.cortes);

    const [filtrosModal, setFiltrosModal] = useState(false);
    const [filtros, setFiltros] = useState(filtrosVacios);

    useEffect(() => {
        dispatch(buscarCortes(filtrosVacios));
        dispatch(buscarSucursales());
        dispatch(buscarEmpleados());
        dispatch(editarBreadcrumb([
            { nombre: 'Cortes', path: '/cortes', activo: true }
        ]));
    }, []);

    const handleAplicarFiltros = (nuevosFiltros) => {
        setFiltros(nuevosFiltros);
        dispatch(buscarCortes(nuevosFiltros));
    };

    const filtrosActivos = [
        filtros.folio,
        filtros.responsable,
        filtros.sucursal,
        filtros.status !== null,
        filtros.fecha_inicio,
        filtros.fecha_fin
    ].filter(Boolean).length;

    const columns = [
        {
            name: 'Folio',
            sortable: true,
            wrap: true, 
            selector: row => row.folio || '-'
        },
        {
            name: 'Responsable',
            sortable: true,
            wrap: true, 
            selector: row => formatNombre(row.responsable),
            grow: 2
        },
        {
            name: 'Sucursal',
            sortable: true,
            wrap: true, 
            selector: row => row.sucursal?.nombre || '-',
            grow: 1.5
        },
        {
            name: 'Total ventas',
            sortable: true,
            wrap: true, 
            selector: row => row.total_ventas != null ? formatNumber(row.total_ventas, 2, '$', '') : '-'
        },
        {
            name: 'Total ventas amb.',
            sortable: true,
            wrap: true, 
            selector: row => row.total_vueltas != null ? formatNumber(row.total_vueltas, 2, '$', '') : '-'
        },
        {
            name: 'Total gastos',
            sortable: true,
            wrap: true, 
            selector: row => row.total_gastos != null ? formatNumber(row.total_gastos, 2, '$', '') : '-'
        },
        {
            name: 'Total conos',
            sortable: true,
            wrap: true, 
            selector: row => row.total_conos != null ? formatNumber(row.total_conos, 0, '', '') : '-'
        },
        {
            name: 'Fecha',
            sortable: true,
            wrap: true, 
            grow: 1.5,
            selector: row => formatFecha(row.fecha)
        },
        {
            name: 'Status',
            sortable: true,
            wrap: true, 
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {row.status === true ? 'Finalizado' : 'Pendiente'}
                </span>
            )
        },
        {
            name: 'Acciones',
            cell: row => (
                <Link className="btn btn-sm btn-info" to={`/cortes/informacion/${row._id}`}>
                    <i className="fas fa-eye"></i>
                </Link>
            )
        }
    ];

    return (
        <div className="row mt-5">
            <div className="col-12 mb-4 d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Cortes de caja</h4>
                <button className="btn btn-secondary position-relative" onClick={() => setFiltrosModal(true)}>
                    <i className="fas fa-filter me-1"></i> Filtros
                    {filtrosActivos > 0 && (
                        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary">
                            {filtrosActivos}
                        </span>
                    )}
                </button>
            </div>

            <div className="col-12">
                <DataTable
                    columns={columns}
                    data={listado}
                    pagination
                    paginationPerPage={20}
                    paginationComponentOptions={{ rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' }}
                    highlightOnHover
                    noDataComponent="No hay cortes registrados"
                />
            </div>

            <FiltrosCorte
                show={filtrosModal}
                handleCerrar={() => setFiltrosModal(false)}
                filtros={filtros}
                handleAplicar={handleAplicarFiltros}
            />
        </div>
    );
};

export default Cortes;
